import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { financeApi } from '@/api/finance.api';
import { showApiError } from '@/utils/apiError';
import type { TreasurySummary } from '@/types/finance';
import { StatCard } from '@/components/ui/StatCard';
import { KpiGrid } from '@/components/ui/KpiGrid';

export function FinanceTreasuryPage() {
  const [summary, setSummary] = useState<TreasurySummary | null>(null);
  const [forecast, setForecast] = useState<Record<string, number> | null>(null);

  useEffect(() => {
    financeApi.treasurySummary().then(setSummary).catch((e) => showApiError(e, 'Failed to load treasury'));
    financeApi.cashForecast(30).then(setForecast).catch(() => setForecast(null));
  }, []);

  return (
    <div>
      <PageHeader
        title="Treasury"
        description="Cash position, liquidity, bank reconciliation status, and forecast"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'Finance', to: '/finance' }, { label: 'Treasury' }]}
      />
      <KpiGrid>
        <StatCard title="Bank accounts" value={summary?.bankAccountCount ?? '—'} />
        <StatCard title="Unreconciled txns" value={summary?.unreconciledTransactions ?? '—'} />
        <StatCard title="Open receivables" value={summary?.openReceivables?.toFixed(2) ?? '—'} />
        <StatCard title="Open payables" value={summary?.openPayables?.toFixed(2) ?? '—'} />
        <StatCard title="Net liquidity" value={summary?.netLiquidityIndicator?.toFixed(2) ?? '—'} />
      </KpiGrid>
      {forecast && (
        <div className="card p-4 mt-4">
          <div className="flex items-center justify-between gap-3 mb-2">
            <h3 className="font-semibold">30-day cash forecast</h3>
            <Link to="/finance/treasury/forecast" className="text-sm text-primary-600 hover:underline">
              Full forecast →
            </Link>
          </div>
          <p className="text-sm">Expected inflows: {forecast.expectedInflows?.toFixed(2)}</p>
          <p className="text-sm">Expected outflows: {forecast.expectedOutflows?.toFixed(2)}</p>
          <p className="text-sm font-medium">Projected net: {forecast.projectedNet?.toFixed(2)}</p>
        </div>
      )}
    </div>
  );
}
